import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getModelIdForCanvas } from "domains/assets/hooks/useAssetLink";
import { extractFirstQueryParam } from "domains/commons/misc";
import {
  InferenceCreateContextValue,
  useInferenceCreateContext,
} from "domains/inference/contexts/InferenceProvider";
import usePresets from "domains/inference/hooks/usePresets";
import useLastModelId from "domains/models/hooks/useLastModelId";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelPresetsByModelIdApiResponse,
  GetModelsByModelIdApiResponse,
  GetModelsInferencesByModelIdAndInferenceIdApiResponse,
  useLazyGetAssetsByAssetIdQuery,
  useLazyGetModelsByModelIdQuery,
  useLazyGetModelsInferencesByModelIdAndInferenceIdQuery,
} from "infra/api/generated/api";
import _ from "lodash";

export default function useImportParamsInference({
  isCanvas = false,
  isTexture = false,
  onValueChange,
}: {
  isCanvas?: boolean;
  isTexture?: boolean;
  onValueChange?: (key: string) => void;
}) {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const [lastModelId] = useLastModelId(isTexture ? "texture" : "image");
  const [lastImportedPresetModelId, setLastImportedPresetModelId] = useState<
    string | undefined
  >();

  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();
  const [getInferenceById] =
    useLazyGetModelsInferencesByModelIdAndInferenceIdQuery();
  const [getModelById] = useLazyGetModelsByModelIdQuery();

  const inferenceCreateContext = useInferenceCreateContext();
  const {
    setIsImportingAsset,
    setPrompt,
    setNegativePrompt,
    setReferenceImage,
    reset,
    setModel,
    setModelFromModelId,
    model,
    isInCanvas,
    setReferenceFromInference,
    isCanvasLiveMode,
  } = inferenceCreateContext;
  const inferenceCreateContextRef = useRef<InferenceCreateContextValue>({
    ...inferenceCreateContext,
  });
  useEffect(() => {
    inferenceCreateContextRef.current = inferenceCreateContext;
  });

  const { defaultPreset } = usePresets({
    modelId: !isInCanvas && model ? model.id : undefined,
  });

  const urlParamsRef = useRef<{
    importAssetId?: string;
    importPromptFromAssetId?: string;
    modelId?: string;
  }>({});
  const urlParams = useMemo(
    () =>
      router.isReady
        ? {
            importAssetId: extractFirstQueryParam(router.query.importAssetId),
            importPromptFromAssetId: extractFirstQueryParam(
              router.query.importPromptFromAssetId
            ),
            modelId: extractFirstQueryParam(router.query.modelId),
          }
        : {},
    [
      router.isReady,
      router.query.importAssetId,
      router.query.importPromptFromAssetId,
      router.query.modelId,
    ]
  );

  // ----------------------------------

  const getAsset = useCallback(
    async (assetId: string) => {
      try {
        const assetData = await getAssetById({
          assetId,
          projectId: selectedProject.id,
        }).unwrap();
        return assetData.asset;
      } catch (_) {}
    },
    [getAssetById, selectedProject.id]
  );

  const getInference = useCallback(
    async (modelId: string, inferenceId: string) => {
      try {
        const inferenceData = await getInferenceById({
          modelId,
          inferenceId,
          projectId: selectedProject.id,
        }).unwrap();
        return inferenceData.inference;
      } catch (_) {}
    },
    [getInferenceById, selectedProject.id]
  );

  const getModel = useCallback(
    async (modelId: string) => {
      if (!modelId) {
        return;
      }
      try {
        const modelData = await getModelById({
          modelId,
          projectId: selectedProject.id,
        }).unwrap();
        return modelData.model;
      } catch (_) {}
    },
    [getModelById, selectedProject.id]
  );

  const importDefaultParams = useCallback(
    async ({
      inference,
      model,
    }: {
      inference:
        | GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"]
        | GetModelPresetsByModelIdApiResponse["presets"][0]
        | undefined;
      model: GetModelsByModelIdApiResponse["model"] | undefined;
    }) => {
      if (model) {
        setModel(model, {
          avoidDimensionChange: true,
        });
      }

      if (inference) {
        return new Promise((resolve) => {
          setTimeout(() => {
            const {
              setConcepts,
              setHeight,
              setWidth,
              setNegativePrompt,
              setPrompt,
              setBaseModelId,
            } = inferenceCreateContextRef.current;

            if (inference.parameters.concepts) {
              setConcepts(inference.parameters.concepts);
            }
            if (inference.parameters.height) {
              setHeight(inference.parameters.height);
            }
            if (inference.parameters.width) {
              setWidth(inference.parameters.width);
            }
            if (inference.parameters.negativePrompt) {
              setNegativePrompt(inference.parameters.negativePrompt);
              onValueChange?.("negativePrompt");
            }
            if (inference.parameters.prompt) {
              setPrompt(inference.parameters.prompt);
            }
            if (inference.parameters.baseModelId) {
              setBaseModelId(inference.parameters.baseModelId);
            }

            resolve(undefined);
          }, 1);
        });
      }
    },
    [setModel, onValueChange]
  );

  const importParamsForRefine = useCallback(
    async ({
      asset,
      inference,
      model,
    }: {
      asset: GetAssetsByAssetIdApiResponse["asset"];
      inference:
        | GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"]
        | undefined;
      model: GetModelsByModelIdApiResponse["model"] | undefined;
    }) => {
      reset();
      await importDefaultParams({ inference, model });
      await setReferenceImage("reference", asset, {
        preventDimensionsUpdate: true,
      });
    },
    [reset, importDefaultParams, setReferenceImage]
  );

  const importParamsForRestartGeneration = useCallback(
    async ({
      inference,
      model,
    }: {
      inference:
        | GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"]
        | GetModelPresetsByModelIdApiResponse["presets"][0];
      model: GetModelsByModelIdApiResponse["model"] | undefined;
    }) => {
      reset();
      await importDefaultParams({ inference, model });
      await setReferenceFromInference(inference);

      return new Promise((resolve) => {
        setTimeout(() => {
          const {
            setScheduler,
            setSamplingSteps,
            setGuidance,
            setSeed,
            setNbImages,
            setControlEnd,
          } = inferenceCreateContextRef.current;

          setScheduler(inference.parameters.scheduler);

          if (inference.parameters.numInferenceSteps) {
            setSamplingSteps(inference.parameters.numInferenceSteps);
          }
          if (inference.parameters.guidance) {
            setGuidance(inference.parameters.guidance);
          }
          if (inference.parameters.seed) {
            setSeed(inference.parameters.seed);
          }

          if (inference.parameters.numSamples) {
            setNbImages(inference.parameters.numSamples);
          }

          if (inference.parameters.controlEnd) {
            setControlEnd(inference.parameters.controlEnd);
          }

          resolve(undefined);
        }, 1);
      });
    },
    [reset, importDefaultParams, setReferenceFromInference]
  );

  const importPreset = useCallback(async () => {
    if (
      model &&
      model.id !== lastImportedPresetModelId &&
      defaultPreset &&
      defaultPreset.modelId === model.id
    ) {
      setLastImportedPresetModelId(model.id);

      await importParamsForRestartGeneration({
        inference: {
          ...defaultPreset,
          ...(isCanvas && {
            inferenceType: undefined,
            image: undefined,
            imageId: undefined,
            controlImage: undefined,
            controlImageId: undefined,
            ipAdapterImage: undefined,
            ipAdapterImageId: undefined,
            width: undefined,
            height: undefined,
          }),
        },
        model,
      });
    }
  }, [
    defaultPreset,
    importParamsForRestartGeneration,
    isCanvas,
    lastImportedPresetModelId,
    model,
  ]);

  const importAssetFromId = useCallback(
    async (assetId: string) => {
      try {
        setIsImportingAsset(true);

        if (assetId.includes("|")) {
          const [modelId, inferenceId, imageIndex] = assetId.split("|");

          const inference = await getInference(modelId, inferenceId);
          if (!inference) throw new Error("NOT_FOUND");

          if (imageIndex !== undefined) {
            const upToDateImportId = inference.images[parseInt(imageIndex)].id;
            Track(AnalyticsEvents.Inference.UsedDeprecatedImportAssetId);
            void router.replace({
              pathname: router.pathname,
              query: {
                ...router.query,
                importAssetId: upToDateImportId,
              },
            });
            throw new Error("DEPRECATED_IMPORT");
          }

          const model = await getModel(
            isInCanvas
              ? getModelIdForCanvas(modelId, { isLive: isCanvasLiveMode })
              : modelId
          );
          await importParamsForRestartGeneration({
            inference,
            model,
          });
          return;
        }

        const asset = await getAsset(assetId);
        if (!asset) throw new Error("NOT_FOUND");

        const [inference, model] = await (async () => {
          if (!(asset.metadata.modelId && asset.metadata.inferenceId)) {
            return [undefined, undefined];
          } else {
            return [
              await getInference(
                asset.metadata.modelId,
                asset.metadata.inferenceId
              ),
              await getModel(
                isInCanvas
                  ? getModelIdForCanvas(asset.metadata.modelId, {
                      isLive: isCanvasLiveMode,
                    })
                  : asset.metadata.modelId
              ),
            ];
          }
        })();

        await importPreset();
        await importParamsForRefine({
          asset,
          inference,
          model,
        });
      } catch (err: any) {
        if (err.message === "NOT_FOUND") return;
        if (err.message === "DEPRECATED_IMPORT") return;
        throw err;
      } finally {
        setTimeout(() => {
          setIsImportingAsset(false);
        }, 1);
      }
    },
    [
      getAsset,
      getInference,
      getModel,
      importParamsForRefine,
      importParamsForRestartGeneration,
      importPreset,
      router,
      setIsImportingAsset,
      isInCanvas,
      isCanvasLiveMode,
    ]
  );

  const importPromptFromAssetId = useCallback(
    async (assetId: string) => {
      try {
        setIsImportingAsset(true);

        const asset = await getAsset(assetId);
        if (!asset) throw new Error("NOT_FOUND");

        setPrompt(asset.metadata.prompt ?? "");
        setNegativePrompt(asset.metadata.negativePrompt ?? "");
      } catch (err: any) {
        if (err.message === "NOT_FOUND") return;
        throw err;
      } finally {
        setTimeout(() => {
          setIsImportingAsset(false);
        }, 1);
      }
    },
    [getAsset, setIsImportingAsset, setNegativePrompt, setPrompt]
  );

  // ----------------------------------

  useEffect(() => {
    if (_.isEqual(urlParams, urlParamsRef.current)) {
      return;
    }

    const prevUrlParams = { ...urlParamsRef.current };
    urlParamsRef.current = urlParams;

    if (
      urlParams.importAssetId ||
      urlParams.importPromptFromAssetId ||
      !urlParams.modelId
    ) {
      void router.replace({
        pathname: router.pathname,
        query: {
          ..._.omit(router.query, "importAssetId", "importPromptFromAssetId"),
          modelId: urlParams.modelId ?? (isInCanvas ? undefined : lastModelId),
        },
      });
    }

    setTimeout(async () => {
      if (urlParams.importAssetId) {
        try {
          await importAssetFromId(urlParams.importAssetId);
          return;
        } catch {}
      }

      if (urlParams.importPromptFromAssetId) {
        void importPromptFromAssetId(urlParams.importPromptFromAssetId);
      }

      if (urlParams.modelId && urlParams.modelId !== prevUrlParams.modelId) {
        void setModelFromModelId(urlParams.modelId);
      } else if (!urlParams.modelId && !isInCanvas) {
        void setModelFromModelId(lastModelId);
      }
    }, 1);
  }, [
    importAssetFromId,
    importPreset,
    importPromptFromAssetId,
    router,
    setModelFromModelId,
    urlParams,
    lastModelId,
    isInCanvas,
  ]);
}
